import { useCallback, useMemo, useRef } from 'react';
import { Chart } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  Tooltip,
} from 'chart.js';
import PropTypes from 'prop-types';
import 'chartjs-plugin-style';
import { useChartSupportValues } from 'hooks/useChartSupportValues';
import { getTooltipLabel } from 'helpers/charts';
import commentImage from 'assets/icons/comment.png';
import { getMonthIndexFromName } from 'helpers/monthSelector';
import { useSimpleMedia } from 'hooks/useMedia';
import { HR_TABS_IDS } from 'const/HRPage';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  Tooltip
);

const { CA, TOTAL_COST } = HR_TABS_IDS;

const semiTransparentColors = {
  0: 'rgba(10,169,188,0.5)',
  1: 'rgba(162,205,111,0.5)',
  2: 'rgba(184,184,184,0.5)',
};

Tooltip.positioners.cursor = (_, coordinates) => coordinates;

const propTypes = {
  data: PropTypes.array.isRequired,
  hiddenLinesYears: PropTypes.array,
  activeMonthName: PropTypes.string.isRequired,
  activeYear: PropTypes.number.isRequired,
  activeDateViewOption: PropTypes.string.isRequired,
  firstMonth: PropTypes.string.isRequired,
  enableGrid: PropTypes.bool,
  comments: PropTypes.array,
  openCommentsModal: PropTypes.func,
  dashboardName: PropTypes.string,
  activeTabId: PropTypes.string,
  showLastDayOfMonthOnTooltipInAnnualMode: PropTypes.bool,
};

const commentsImage = new Image();
commentsImage.src = commentImage;
commentsImage.width = 20;
commentsImage.height = 20;

const SCREEN_WIDTH_BREAKPOINT_VALUE = 950;
const SCREEN_WIDTH_BREAKPOINT_MEDIA = `(max-width: ${SCREEN_WIDTH_BREAKPOINT_VALUE}px)`;

/**
 * @param data {array} - data for chart with following
 *                      structure [{year:number, values:Array<number>>}]
 *                  if its Year View values starts from firstmonth
 *                  e.g first month sept -> values [ 0,  1,  2, ... , 11]
 *                                                 sep, oct,nov,      aug
 * @param hiddenLinesYears {array<Number>}
 * @param activeMonthName {string} - active month in MonthSelector from January to December
 * @param activeYear {number}
 * @param activeDateViewOption {string} - date mode either Annuels(YEAR) or Mensuels(MONTH) from const
 * @param firstMonth {string} - name of first month of financial year from January to December
 * @param comments {array} - array same as values of data above, but if comments exist value 0, if not value null
 * @param enableGrid {boolean} - is grid enabled
 * @param dashboardName {string} - one of 'treasury' 'turnover' 'debtors' 'creditors'
 * @param activeTabId {string} - current tab name, default ''
 * @param openCommentsModal {func} -> function that setup comments modal and opens it
 * @param showLastDayOfMonthOnTooltipInAnnualMode -  when enabled: instead of Month Year label will be dd.mm.yyyy in tooltip
 *                                                    e.g. instead Fevr 2019  -> 28.02.2019
 * */
export const BarChart = ({
  data = [],
  hiddenLinesYears = [],
  activeMonthName,
  activeYear,
  activeDateViewOption,
  firstMonth,
  enableGrid = false,
  comments = [],
  openCommentsModal = () => {},
  dashboardName = '',
  activeTabId = '',
  showLastDayOfMonthOnTooltipInAnnualMode = false,
  ...restProps
}) => {
  const barChartRef = useRef(null);
  const { labels, setHoveredLineIndex, hoveredLineIndex, isViewModeAnnual } =
    useChartSupportValues(
      activeDateViewOption,
      activeYear,
      activeMonthName,
      firstMonth
    );

  const isMobileVersion = useSimpleMedia(SCREEN_WIDTH_BREAKPOINT_MEDIA);

  const dataWithComments = useMemo(() => [{}, ...data], [data]);

  const minValueOfChartData = useMemo(() => {
    let min = 0;
    data.forEach(({ values }) => {
      values.forEach((value) => {
        if (value < min) min = value;
      });
    });
    return min;
  }, [data]);

  const commentChartValue = useMemo(() => {
    const needToAddTo1000 =
      minValueOfChartData % 1000 === 0
        ? 0
        : 1000 - Math.abs(minValueOfChartData % 1000);
    return minValueOfChartData - needToAddTo1000;
  }, [minValueOfChartData]);

  const chartData = useMemo(
    () => ({
      labels,
      datasets: dataWithComments.map(({ id, year, values }, index) => {
        if (hiddenLinesYears.includes(year)) return { label: id, hidden: true };
        if (index === 0) {
          return {
            hidden: !!isMobileVersion,
            label: `comments${activeDateViewOption}${dashboardName}`,
            type: 'line',
            data: labels.map(() => commentChartValue),
            borderWidth: 10,
            pointStyle: commentsImage,
            pointBackgroundColor: labels.map((comment, commentIndex) => {
              if (comments[commentIndex] === 0) return 'rgba(0,0,0,1)';
              return 'rgba(0,0,0,0)';
            }),
            borderColor: 'transparent',
            backgroundColor: 'transparent',
            pointRadius: 0,
            pointHoverRadius: 10,
            pointHoverBackgroundColor: 'white',
            pointBorderColor: 'white',
          };
        }
        return {
          label: id,
          data: values,
          borderWidth: 0,
          borderColor: 'transparent',
          backgroundColor: semiTransparentColors[index - 1],
          hidden: false,
          borderRadius: 6,
          borderSkipped: 'false',
        };
      }),
    }),
    [
      activeDateViewOption,
      commentChartValue,
      comments,
      dashboardName,
      dataWithComments,
      hiddenLinesYears,
      isMobileVersion,
      labels,
    ]
  );

  const onClick = useCallback(
    (e) => {
      const [elem] = barChartRef.current.getElementsAtEventForMode(
        e,
        'nearest',
        { intersect: true },
        false
      );

      if (elem && elem.datasetIndex === 0) {
        if (labels.length === 12) {
          const monthDependsOfFirstMonth = elem.index;
          const firstMonthIndex = getMonthIndexFromName(firstMonth);
          const month =
            firstMonthIndex + monthDependsOfFirstMonth > 11
              ? firstMonthIndex + monthDependsOfFirstMonth - 12
              : firstMonthIndex + monthDependsOfFirstMonth;
          openCommentsModal({
            month,
            day: null,
            chartType: dashboardName.toLowerCase(),
            dateMode: 'month',
          });
        } else {
          const day = elem.index + 1;
          const month = getMonthIndexFromName(activeMonthName);
          openCommentsModal({
            month,
            day,
            chartType: dashboardName.toLowerCase(),
            dateMode: 'day',
          });
        }
      }
    },
    [
      activeMonthName,
      dashboardName,
      firstMonth,
      labels.length,
      openCommentsModal,
    ]
  );

  return (
    <Chart
      ref={barChartRef}
      type="bar"
      {...restProps}
      data={chartData}
      options={{
        events: ['mousemove', 'click'],
        onClick,
        maintainAspectRatio: false,
        responsive: true,
        animation: {
          duration: 0,
        },
        hover: {
          mode: 'point',
          intersect: false,
        },
        onHover(e, item) {
          e.native.target.style.cursor = item[0] ? 'pointer' : 'default';
          if (item.length) {
            setHoveredLineIndex(item[0].datasetIndex);
          } else {
            setHoveredLineIndex(null);
          }
        },

        scales: {
          x: {
            position: data.every((dataset) =>
              dataset.values.every((value) => value <= 0)
            )
              ? 'top'
              : 'bottom',
            grid: {
              color: enableGrid ? 'rgba(0,0,0,0.05)' : 'rgba(0, 0, 0, 0)',
            },
            ticks: {
              color: '#959595',
              font: {
                family: 'Roboto, sans-serif',
                size: 13,
              },
              // maxRotation: 0,
              // autoSkipPadding: 4,
            },
          },
          y: {
            beginAtZero: true,
            ticks: {
              color: '#959595',
              font: {
                family: 'Roboto, sans-serif',
                size: 14,
              },
              callback(value) {
                if (value === 0) return `${value}  `;
                if (value === 1) return '';
                const currencySign =
                  ['TURNOVER', 'TREASURY', 'DEBTORS', 'CREDITORS'].includes(
                    dashboardName
                  ) || [CA, TOTAL_COST].includes(activeTabId)
                    ? '€'
                    : '';
                return `${Math.round(value / 1000)} K${currencySign}`;
              },

              stepSize: function (context) {
                const chart = context.chart;
                const maxValue = chart.scales.y.max;
                const minValue = chart.scales.y.min;
                const range = maxValue - minValue;

                if (range > 1000000) return 250000;
                if (range > 100000) return 50000;
                if (range > 10000) return 5000;
                if (range > 1000) return 500;
                return 100;
              },

              maxTicksLimit: 10,
            },
          },
        },
        legend: {
          display: false,
        },

        interaction: {
          intersect: false,
          mode: 'index',
        },

        plugins: {
          tooltip: {
            enabled: hoveredLineIndex !== 0,
            position: 'nearest',
            intersect: true,
            bodySpacing: 5,
            bodyFont: { family: 'Roboto, sans-serif', size: 13 },
            animation: false,
            callbacks: {
              labelColor(item) {
                const index = item.datasetIndex;
                if (index === 0) return '';
                const color = semiTransparentColors[index - 1];
                return {
                  borderColor: color,
                  backgroundColor: color,
                };
              },
              label(item) {
                if (item.value === 'NaN') return '';
                if (item.datasetIndex === 0) return '';
                return getTooltipLabel(
                  item,
                  activeMonthName,
                  activeYear,
                  dataWithComments,
                  isViewModeAnnual,
                  showLastDayOfMonthOnTooltipInAnnualMode,
                  firstMonth
                );
              },
              labelTextColor() {
                return '#ffffff';
              },
              title() {
                return '';
              },
            },
          },
        },
      }}
    />
  );
};

BarChart.propTypes = propTypes;
